import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import { generateBookingUuid } from '../../utils/autodiag';
import { formatSupplement } from './bookingRequest';
import {
  BoutonLigne,
  BoutonVert,
  Corps,
  Icone,
  Pied,
  SousTitre,
  Titre,
  icone,
  mobile,
} from './ui';

/**
 * Étape 1 : où, puis quoi (retour de Clément du 21/09 : l'adresse d'abord,
 * le problème ensuite, plus petit ; aucune prestation dans le tunnel).
 *
 * Les suggestions et leur détail viennent de l'atelier visé
 * (`/v2/public/shops/{shop}/addresses`, FIXERLIGHT-048), sous un même jeton
 * de session Places. Choisir une suggestion SITUE l'adresse : latitude,
 * longitude et place_id remontent au tunnel (`onPosition`), qui les envoie
 * avec la demande (FIXERLIGHT-073). Un atelier avec zone refuse une adresse
 * sans position ; une adresse retouchée à la main perd la sienne.
 *
 * `couverture`/`couvertureEtat` : la lecture de `GET .../coverage`, tenue
 * par le Tunnel dès que la position est connue (FIXERLIGHT-066). Hors de
 * tous les anneaux, « Continuer » refuse AVANT toute grille de créneaux — le
 * refus du POST arrivait sinon après que le client a choisi les siens pour
 * rien. Dans un anneau à supplément, le montant se voit ici, avant les
 * créneaux, et de nouveau au récapitulatif de l'étape 3 (`EtapeCompte`). La
 * coche ✓ ne s'affiche que couverture CONFIRMÉE (`couvertureEtat === 'ok'`) :
 * avant elle, `zone` (texte libre, sans lien avec CETTE adresse) reste
 * affiché en simple information, jamais comme une validation.
 */

const Champ = styled.div`
  position: relative;
`;

const Saisie = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 58px;
  padding: 0 14px;
  border: 2px solid ${(p) => (p.$rempli ? colors.textDark : colors.borderLight)};
  border-radius: 16px;
  background: ${colors.white};

  &:focus-within {
    border-color: ${colors.bleu};
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${fonts.body};
    /* 16px minimum : en dessous, iOS zoome à la mise au point. */
    font-size: 16px;
    color: ${colors.textDark};
  }
`;

const Suggestions = styled.ul`
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  border: 1.5px solid ${colors.borderLight};
  border-radius: 14px;
  overflow: hidden;
  background: ${colors.white};
`;

const Suggestion = styled.li`
  padding: 12px 14px;
  min-height: 48px;
  font-size: 14px;
  line-height: 1.35;
  border-bottom: 1px solid ${mobile.muted};
  cursor: pointer;
  background: ${(p) => (p.$active ? mobile.muted : colors.white)};

  &:last-child {
    border-bottom: none;
  }

  span {
    display: block;
    font-size: 12px;
    color: ${colors.textSecondary};
  }
`;

const Zone = styled.p`
  display: flex;
  gap: 8px;
  margin: 10px 0 0;
  font-size: 13px;
  color: ${colors.textSecondary};

  b {
    color: ${colors.textDark};
    font-weight: 600;
  }
`;

/** Adresse hors de tous les anneaux (FIXERLIGHT-066) : refus AVANT les créneaux. */
const HorsZone = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px 0 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${colors.statusClosedDef};
  color: ${colors.statusClosedDefText};
  font-size: 13.5px;
  line-height: 1.4;
`;

/**
 * Adresse tapée sans suggestion sur un compte `has_coverage` (FIXERLIGHT-066,
 * relecture B1) : ni couverte ni hors zone, juste pas encore SITUÉE. Ton
 * d'avertissement doux (pas une erreur, l'ancien texte reste valide) plutôt
 * que celui, plus dur, de `HorsZone`.
 */
const NonSituee = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px 0 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${colors.statusClosedTemp};
  color: ${colors.statusClosedTempText};
  font-size: 13.5px;
  line-height: 1.4;
`;

const Separateur = styled.hr`
  border: none;
  height: 1px;
  background: ${colors.borderLight};
  margin: 24px 0 20px;
`;

const Libelle = styled.label`
  display: block;
  margin-bottom: 4px;
  font-family: ${fonts.heading};
  font-size: 16px;
  color: ${colors.textDark};
`;

const Aide = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  color: ${colors.textSecondary};
`;

const Texte = styled.textarea`
  width: 100%;
  min-height: 104px;
  padding: 12px 14px;
  border: 1.5px solid ${colors.borderLight};
  border-radius: 14px;
  font-family: ${fonts.body};
  font-size: 16px;
  line-height: 1.45;
  color: ${colors.textDark};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${colors.bleu};
  }
`;

const DELAI_MS = 350;

export default function EtapeAdresse({
  suggerer,
  detailler,
  adresse,
  probleme,
  zone,
  couverture,
  couvertureEtat,
  hasCoverage,
  telAtelier,
  onAdresse,
  onPosition,
  onProbleme,
  onContinuer,
  titreRef,
}) {
  const [saisie, setSaisie] = useState(adresse);
  const [suggestions, setSuggestions] = useState([]);
  const [active, setActive] = useState(-1);
  const [situation, setSituation] = useState(false);
  // Le détail vient d'échouer pour la suggestion qu'on venait de choisir
  // (relecture 2 de FIXERLIGHT-066) : distinct d'une adresse jamais choisie,
  // pour ne pas dire « choisissez parmi les suggestions » à qui vient d'en
  // choisir une.
  const [detailEnPanne, setDetailEnPanne] = useState(false);
  const minuteur = useRef(null);
  const derniere = useRef('');
  // Le jeton de session Places : un par recherche, clos par le détail.
  const session = useRef(generateBookingUuid());
  // Le numéro du choix en cours de résolution : un choix plus récent, ou une
  // frappe, l'annule — sa réponse tardive ne doit pas situer une autre
  // adresse. Un numéro par appel, pas le place_id : rechoisir la même
  // suggestion ouvre un nouvel appel, que l'échec de l'ancien ne doit pas
  // défaire.
  const choixEnCours = useRef(0);

  useEffect(() => () => clearTimeout(minuteur.current), []);

  const chercher = (texte) => {
    clearTimeout(minuteur.current);
    if (texte.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    minuteur.current = setTimeout(async () => {
      derniere.current = texte;
      try {
        const rep = await suggerer(texte, session.current);
        // Une réponse lente ne remplace pas une réponse plus récente.
        if (derniere.current !== texte) return;
        setSuggestions(Array.isArray(rep) ? rep : []);
        setActive(-1);
      } catch (e) {
        setSuggestions([]);
      }
    }, DELAI_MS);
  };

  const changer = (texte) => {
    choixEnCours.current += 1;
    setSituation(false);
    setDetailEnPanne(false);
    setSaisie(texte);
    onAdresse(texte);
    onPosition(null);
    chercher(texte);
  };

  const choisir = async (s) => {
    setSaisie(s.label);
    onAdresse(s.label);
    onPosition(null);
    setSuggestions([]);
    setDetailEnPanne(false);
    choixEnCours.current += 1;
    const choix = choixEnCours.current;
    const jeton = session.current;
    // Le détail clôt la session Places : la recherche suivante en ouvre une autre.
    session.current = generateBookingUuid();
    setSituation(true);
    try {
      const detail = await detailler(s.place_id, jeton);
      if (choixEnCours.current !== choix) return;
      onPosition({
        latitude: detail.latitude,
        longitude: detail.longitude,
        place_id: detail.place_id,
      });
    } catch (e) {
      // Détail introuvable ou panne : l'adresse reste, sans position. Sur un
      // atelier avec zone, l'api la refusera de toute façon à l'envoi — mais
      // dire ici « choisissez parmi les suggestions » à qui vient d'en
      // choisir une n'aiderait pas : un message dédié le distingue.
      if (choixEnCours.current === choix) setDetailEnPanne(true);
    } finally {
      if (choixEnCours.current === choix) setSituation(false);
    }
  };

  // Porte d'entrée 2 (`adresseInitiale`, module de réparation hors zone RJ) :
  // le texte est déjà là au montage, mais `chercher()` ne part qu'au
  // `onChange` — sans cette relance, un compte `has_coverage` dit « choisissez
  // parmi les suggestions » sans jamais en proposer. Une seule fois : `ref`
  // plutôt qu'un simple test sur `adresse`, pour ne jamais relancer une
  // deuxième recherche derrière le dos d'une frappe ou d'un choix en cours.
  // `hasCoverage` peut n'arriver qu'après ce premier rendu (fiche encore en
  // chargement) : les deps le couvrent, sans risque de boucle (le ref bloque
  // tout appel suivant).
  const rechercheInitiale = useRef(false);
  useEffect(() => {
    if (
      rechercheInitiale.current ||
      !hasCoverage ||
      situation ||
      // Adresse déjà située avant ce montage (retour depuis l'étape 2,
      // brouillon repris) : la relancer coûterait un appel Places facturé et
      // rouvrirait la liste sur une adresse valide.
      couvertureEtat !== 'inconnue' ||
      adresse.trim() === ''
    ) {
      return;
    }
    rechercheInitiale.current = true;
    chercher(adresse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCoverage, adresse, situation]);

  const clavier = (e) => {
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      choisir(suggestions[active]);
    } else if (e.key === 'Escape') {
      // Échap ferme la liste, pas le tunnel.
      e.stopPropagation();
      setSuggestions([]);
    }
  };

  // Hors de tous les anneaux : le refus est celui de l'étape 1, pas celui,
  // plus tardif, du POST (FIXERLIGHT-066). `couvertureEtat === 'ok'` exclu
  // `'erreur'` : une panne de /coverage ne bloque pas le client, l'envoi
  // reste le juge (FIXERLIGHT-048 § 4, `assertCovered`).
  const horsZone =
    couvertureEtat === 'ok' && couverture && couverture.covered === false;
  // has_coverage vrai et aucune position connue (`couvertureEtat` retombe à
  // 'inconnue' dès que la position s'efface, cf. l'effet du Tunnel) : sans
  // cette garde, une adresse tapée sans suggestion — ou retouchée après un
  // refus — repartait en texte libre, et c'était alors le POST qui refusait
  // `address_not_located`, après que le client a choisi ses créneaux pour
  // rien (relecture B1). `adresse.trim()` : rien à dire sur un champ encore
  // vide, déjà couvert par `pret` ci-dessous.
  const adresseNonSituee =
    hasCoverage && couvertureEtat === 'inconnue' && adresse.trim() !== '';
  // Pas de « Continuer » pendant qu'une suggestion choisie se situe (partir
  // avant enverrait l'adresse sans sa position), ni pendant que sa couverture
  // se lit : sans cette garde, la fenêtre entre la position connue et la
  // réponse de /coverage laisserait passer un hors-zone.
  const pret =
    adresse.trim() !== '' &&
    probleme.trim() !== '' &&
    !situation &&
    couvertureEtat !== 'chargement' &&
    !horsZone &&
    !adresseNonSituee;

  return (
    <>
      <Corps>
        <Titre tabIndex={-1} ref={titreRef}>
          Où le réparateur doit-il venir ?
        </Titre>
        <SousTitre>
          Le réparateur se déplace chez vous, au travail ou là où est votre
          vélo.
        </SousTitre>

        <Champ>
          <Saisie $rempli={saisie.trim() !== ''}>
            <Icone src={icone('LOCALISATION')} alt="" $taille={20} />
            <input
              id="tunnel-adresse"
              type="text"
              role="combobox"
              aria-label="Adresse d’intervention"
              aria-autocomplete="list"
              aria-expanded={suggestions.length > 0}
              aria-controls="tunnel-adresse-suggestions"
              aria-activedescendant={
                active >= 0 ? `tunnel-adresse-s${active}` : undefined
              }
              autoComplete="off"
              placeholder="Numéro, rue, ville"
              value={saisie}
              onChange={(e) => changer(e.target.value)}
              onKeyDown={clavier}
              /* La sélection passe par mousedown : fermer au blur ne la perd pas. */
              onBlur={() => setSuggestions([])}
            />
          </Saisie>
          {suggestions.length > 0 && (
            <Suggestions id="tunnel-adresse-suggestions" role="listbox">
              {suggestions.map((s, i) => {
                return (
                  <Suggestion
                    key={s.place_id || s.label}
                    id={`tunnel-adresse-s${i}`}
                    role="option"
                    aria-selected={i === active}
                    $active={i === active}
                    /* mousedown et non click : le click arrive après le blur
                       du champ, quand la liste a déjà pu disparaître. */
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choisir(s);
                    }}>
                    {s.main_text || s.label}
                    {s.secondary_text && <span>{s.secondary_text}</span>}
                  </Suggestion>
                );
              })}
            </Suggestions>
          )}
        </Champ>

        {horsZone ? (
          // Refus AVANT toute grille de créneaux (FIXERLIGHT-066) : le POST
          // le referait de toute façon, mais après que le client a choisi
          // les siens pour rien. « Écrire au réparateur » resterait la
          // sortie la plus proche, mais le tunnel ne sait pas encore
          // l'ouvrir depuis ici sans développement propre (FIXERLIGHT-049
          // vit hors du tunnel, sur la fiche) : hypothèse posée, pas ajoutée
          // ici.
          <HorsZone role="alert">
            <b>Ce réparateur n’intervient pas à cette adresse.</b>
            {zone && (
              <span>
                Ce réparateur intervient à <b>{zone}</b>.
              </span>
            )}
            {telAtelier && (
              <BoutonLigne as="a" href={`tel:${telAtelier}`}>
                <Icone src={icone('SMARTPHONE')} alt="" /> Appeler le réparateur
              </BoutonLigne>
            )}
          </HorsZone>
        ) : adresseNonSituee ? (
          // has_coverage vrai, aucune position connue (relecture B1) :
          // l'ancien texte libre ne suffit plus, l'api le refuserait de
          // toute façon à l'envoi (`address_not_located`) — mais après que
          // le client a choisi ses créneaux. Remplace le simple texte
          // d'information `zone` ci-dessous : les deux ne coexistent pas.
          <NonSituee role="status">
            {detailEnPanne ? (
              <span>
                Nous n’arrivons pas à situer cette adresse. Réessayez, ou
                appelez l’atelier.
              </span>
            ) : (
              <span>Choisissez votre adresse parmi les suggestions.</span>
            )}
            {detailEnPanne && telAtelier && (
              <BoutonLigne as="a" href={`tel:${telAtelier}`}>
                <Icone src={icone('SMARTPHONE')} alt="" /> Appeler l’atelier
              </BoutonLigne>
            )}
          </NonSituee>
        ) : couvertureEtat === 'ok' &&
          couverture &&
          couverture.surcharge_eur ? (
          // Couvert, avec supplément : le montant se voit ICI, avant les
          // créneaux, et de nouveau au récapitulatif de l'étape 3.
          <Zone>
            <span aria-hidden="true">✓</span>
            <span>
              L’atelier intervient{' '}
              {zone ? (
                <>
                  à <b>{zone}</b>
                </>
              ) : (
                'à cette adresse'
              )}
              , avec un supplément de déplacement de{' '}
              <b>{formatSupplement(couverture.surcharge_eur)}</b>.
            </span>
          </Zone>
        ) : (
          zone && (
            <Zone>
              {/* La coche ne vaut que pour une couverture CONFIRMÉE : tant que
                  l'adresse n'est pas située (ou que sa lecture est en panne),
                  ce texte reste une simple information, jamais une validation. */}
              {couvertureEtat === 'ok' && <span aria-hidden="true">✓</span>}
              <span>
                L’atelier intervient à <b>{zone}</b>.
              </span>
            </Zone>
          )
        )}

        <Separateur />

        <Libelle htmlFor="tunnel-probleme">Votre problème</Libelle>
        <Aide>En quelques mots, ce que vous avez remarqué.</Aide>
        <Texte
          id="tunnel-probleme"
          placeholder="Ex : ça freine mal à l’avant, la chaîne saute"
          value={probleme}
          onChange={(e) => onProbleme(e.target.value)}
        />
      </Corps>
      <Pied>
        <BoutonVert type="button" disabled={!pret} onClick={onContinuer}>
          Continuer
        </BoutonVert>
      </Pied>
    </>
  );
}
